import { io, Socket } from 'socket.io-client';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface WsProgressUpdate {
  sessionId: string;
  studentId: string;
  stepId: string;
  status: string;
  studentName?: string;
  checklistChecked?: number;
}

export interface WsHelpRequest {
  sessionId: string;
  id: string;
  studentId: string;
  studentName?: string;
  stepId?: string;
  stepTitle?: string;
  stepOrderIndex?: number;
  message?: string;
  status: string;
}

export interface WsHelpResolved {
  sessionId: string;
  id: string;
  studentId: string;
  resolvedById: string;
  response?: string;
}

export interface WsHelpMessage {
  sessionId: string;
  helpRequestId: string;
  messageId: string;
  userId: string;
  userRole: string;
  userName?: string;
  message: string;
  createdAt: string;
  status: string;
}

export interface WsSessionReaction {
  sessionId: string;
  userId: string;
  userName?: string;
  emoji: string;
  stepIndex?: number;
  timestamp: string;
}

export interface WsMessageReaction {
  sessionId: string;
  helpRequestId: string;
  messageId: string;
  userId: string;
  userName?: string;
  emoji: string;
  action: 'added' | 'removed';
}

export interface WsSessionChatMessage {
  sessionId: string;
  userId: string;
  userName: string;
  userRole: 'instructor' | 'student';
  message: string;
  timestamp: number;
}

type StatusCallback = (status: ConnectionStatus) => void;

class SocketService {
  private socket: Socket | null = null;
  private statusListeners: StatusCallback[] = [];
  private _status: ConnectionStatus = 'disconnected';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Socket.IO callbacks require any
  private pendingListeners: Array<{ event: string; callback: (...args: any[]) => void }> = [];

  get status(): ConnectionStatus {
    return this._status;
  }

  private setStatus(status: ConnectionStatus) {
    this._status = status;
    this.statusListeners.forEach((cb) => cb(status));
  }

  onStatusChange(callback: StatusCallback): () => void {
    this.statusListeners.push(callback);
    return () => {
      this.statusListeners = this.statusListeners.filter((cb) => cb !== callback);
    };
  }

  connect(token: string): void {
    if (this.socket) return;

    this.setStatus('connecting');
    const wsUrl = process.env.REACT_APP_WS_URL || 'http://localhost:3001';
    this.socket = io(wsUrl, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    this.socket.on('connect', () => {
      this.setStatus('connected');
      // Apply any queued listeners
      for (const { event, callback } of this.pendingListeners) {
        this.socket!.on(event, callback);
      }
      this.pendingListeners = [];
    });

    this.socket.on('disconnect', () => {
      this.setStatus('disconnected');
    });

    this.socket.on('connect_error', () => {
      this.setStatus('error');
    });

    this.socket.on('error', () => {
      this.setStatus('error');
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.setStatus('disconnected');
  }

  joinSession(sessionId: string): void {
    this.socket?.emit('join-session', { sessionId });
  }

  leaveSession(sessionId: string): void {
    this.socket?.emit('leave-session', { sessionId });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Socket.IO callbacks require any
  private addListener(event: string, callback: (...args: any[]) => void): () => void {
    if (this.socket) {
      this.socket.on(event, callback);
    } else {
      this.pendingListeners.push({ event, callback });
    }
    return () => {
      this.socket?.off(event, callback);
      this.pendingListeners = this.pendingListeners.filter((l) => l.callback !== callback);
    };
  }

  onProgressUpdate(callback: (data: WsProgressUpdate) => void): () => void {
    return this.addListener('progress-update', callback);
  }

  onHelpRequest(callback: (data: WsHelpRequest) => void): () => void {
    return this.addListener('help-request', callback);
  }

  onHelpResolved(callback: (data: WsHelpResolved) => void): () => void {
    return this.addListener('help-resolved', callback);
  }

  onHelpMessage(callback: (data: WsHelpMessage) => void): () => void {
    return this.addListener('help-message', callback);
  }

  onSessionBroadcast(callback: (data: { sessionId: string; message: string }) => void): () => void {
    return this.addListener('session-broadcast', callback);
  }

  sendBroadcast(sessionId: string, message: string): void {
    this.socket?.emit('broadcast-message', { sessionId, message });
  }

  sendReaction(sessionId: string, emoji: string, stepIndex?: number): void {
    this.socket?.emit('send-reaction', { sessionId, emoji, stepIndex });
  }

  onSessionReaction(callback: (data: WsSessionReaction) => void): () => void {
    return this.addListener('session-reaction', callback);
  }

  onMessageReaction(callback: (data: WsMessageReaction) => void): () => void {
    return this.addListener('message-reaction', callback);
  }

  sendChatMessage(sessionId: string, message: string): void {
    this.socket?.emit('send-chat-message', { sessionId, message });
  }

  onSessionChatMessage(callback: (data: WsSessionChatMessage) => void): () => void {
    return this.addListener('session-chat-message', callback);
  }

  onSessionEnded(callback: (data: { sessionId: string }) => void): () => void {
    return this.addListener('session-ended', callback);
  }

  onSessionPaused(callback: (data: { sessionId: string }) => void): () => void {
    return this.addListener('session-paused', callback);
  }

  onSessionResumed(callback: (data: { sessionId: string }) => void): () => void {
    return this.addListener('session-resumed', callback);
  }

  // Instructor presence
  emitPresence(sessionId: string, page: string): void {
    this.socket?.emit('instructor-presence', { sessionId, page });
  }

  onPresenceUpdate(callback: (data: { instructors: Array<{ instructorId: string; name: string; page: string }> }) => void): () => void {
    return this.addListener('instructor-presence-update', callback);
  }

  // Whisper chat
  sendWhisper(sessionId: string, message: string): void {
    this.socket?.emit('whisper-message', { sessionId, message });
  }

  onWhisperMessage(callback: (data: { senderId: string; senderName: string; message: string; timestamp: string }) => void): () => void {
    return this.addListener('whisper-broadcast', callback);
  }

  // Help request claiming
  claimHelpRequest(sessionId: string, requestId: string): void {
    this.socket?.emit('help-request-claim', { sessionId, requestId });
  }

  unclaimHelpRequest(sessionId: string, requestId: string): void {
    this.socket?.emit('help-request-unclaim', { sessionId, requestId });
  }

  getActiveClaims(sessionId: string): void {
    this.socket?.emit('get-active-claims', { sessionId });
  }

  onHelpRequestClaimed(callback: (data: { requestId: string; claimedBy: { id: string; name: string } }) => void): () => void {
    return this.addListener('help-request-claimed', callback);
  }

  onHelpRequestUnclaimed(callback: (data: { requestId: string }) => void): () => void {
    return this.addListener('help-request-unclaimed', callback);
  }

  onActiveClaims(callback: (data: { claims: Array<{ requestId: string; claimedBy: { id: string; name: string } }> }) => void): () => void {
    return this.addListener('active-claims', callback);
  }
}

export const socketService = new SocketService();
